import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePlanRestrictions } from './usePlanRestrictions';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface PlanLimits {
  maxClients: number;
  maxOrdersPerMonth: number;
  currentClients: number;
  currentMonthOrders: number;
  canAddClient: boolean;
  canAddOrder: boolean;
}

interface UsePlanLimitsReturn {
  limits: PlanLimits | null;
  loading: boolean;
  error: string | null;
  checkClientLimit: () => Promise<boolean>;
  checkOrderLimit: () => Promise<boolean>;
  refreshLimits: () => Promise<void>;
}

export const usePlanLimits = (): UsePlanLimitsReturn => {
  const { user } = useAuth();
  const { restrictions } = usePlanRestrictions();
  const navigate = useNavigate();
  const [limits, setLimits] = useState<PlanLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLimits = useCallback(async () => {
    console.log('📊 Fetching plan limits...');
    
    if (!user) {
      console.log('❌ No user found for plan limits');
      setLimits(null);
      setLoading(false);
      return;
    }

    // Prevent duplicate fetches
    if (loading) {
      console.log('⏳ Already fetching limits, skipping...');
      return;
    }

    // Vérifier le rôle admin directement depuis la base de données
    let userIsAdmin = false;
    if (isSupabaseConfigured && supabase) {
      try {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!profileError && profile) {
          const normalizedRole = profile.role?.toLowerCase();
          userIsAdmin = ['admin', 'moderator'].includes(normalizedRole || '');
        }
      } catch (err) {
        console.error('Error checking admin role in fetchLimits:', err);
      }
    }

    // If user is admin or not on free plan, no limits apply
    if (userIsAdmin || restrictions?.isAdmin || restrictions?.plan !== 'free') {
      console.log('✅ User is admin or not on free plan, no limits apply');
      setLimits({
        maxClients: Infinity,
        maxOrdersPerMonth: Infinity,
        currentClients: 0,
        currentMonthOrders: 0,
        canAddClient: true,
        canAddOrder: true
      });
      setLoading(false);
      return;
    }

    // If Supabase is not configured, return empty limits
    if (!isSupabaseConfigured || !supabase) {
      console.error('Supabase not configured - cannot fetch plan limits');
      setLimits({
        maxClients: 0,
        maxOrdersPerMonth: 0,
        currentClients: 0,
        currentMonthOrders: 0,
        canAddClient: false,
        canAddOrder: false
      });
      setLoading(false);
      return;
    }

    try {
      setError(null);
      
      // Get current client count
      console.log('🔍 Counting current clients...');
      const { count: clientCount, error: clientError } = await supabase
        .from('clients')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (clientError) {
        console.error('❌ Error counting clients:', clientError);
        throw clientError;
      }

      // Get current month order count
      console.log('🔍 Counting current month orders...');
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const { count: orderCount, error: orderError } = await supabase
        .from('orders')
        .select('client_id, clients!inner(user_id)', { count: 'exact', head: true })
        .eq('clients.user_id', user.id)
        .gte('created_at', startOfMonth.toISOString())
        .lte('created_at', endOfMonth.toISOString());

      if (orderError) {
        console.error('❌ Error counting orders:', orderError);
        throw orderError;
      }

      const currentClients = clientCount || 0;
      const currentMonthOrders = orderCount || 0;
      const maxClients = 5;
      const maxOrdersPerMonth = 10;

      const planLimits: PlanLimits = {
        maxClients,
        maxOrdersPerMonth,
        currentClients,
        currentMonthOrders,
        canAddClient: currentClients < maxClients,
        canAddOrder: currentMonthOrders < maxOrdersPerMonth
      };

      console.log('✅ Plan limits loaded:', planLimits);
      setLimits(planLimits);
    } catch (error) {
      console.error('💥 Error fetching plan limits:', error);
      setError(error instanceof Error ? error.message : 'Failed to load plan limits');
      // Fallback to safe defaults
      setLimits({
        maxClients: 5,
        maxOrdersPerMonth: 10,
        currentClients: 0,
        currentMonthOrders: 0,
        canAddClient: true,
        canAddOrder: true
      });
    } finally {
      setLoading(false);
    }
  }, [user, restrictions]);

  const showLimitError = (type: 'client' | 'order') => {
    const message = type === 'client' 
      ? 'Free plan limit of 5 clients reached. Please upgrade to continue.'
      : 'Free plan limit of 10 orders per month reached. Please upgrade to continue.';
    
    toast.error(message, {
      duration: 6000,
      action: {
        label: 'Upgrade',
        onClick: () => navigate('/upgrade')
      }
    });
    
    // Redirect to upgrade page after a short delay
    setTimeout(() => {
      navigate('/upgrade');
    }, 2000);
  };

  const checkClientLimit = useCallback(async (): Promise<boolean> => {
    console.log('🔍 Checking client limit...');
    
    // Vérifier d'abord le rôle admin directement depuis la base de données
    let userIsAdmin = false;
    if (user && isSupabaseConfigured && supabase) {
      try {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!profileError && profile) {
          const normalizedRole = profile.role?.toLowerCase();
          userIsAdmin = ['admin', 'moderator'].includes(normalizedRole || '');
        }
      } catch (err) {
        console.error('Error checking admin role in checkClientLimit:', err);
      }
    }
    
    // Si admin ou pas sur le plan free, autoriser
    if (userIsAdmin || restrictions?.isAdmin || restrictions?.plan !== 'free') {
      console.log('✅ User is admin or not on free plan, bypassing limit');
      return true;
    }

    // Refresh limits to get latest count
    await fetchLimits();
    
    if (!limits?.canAddClient) {
      console.log('❌ Client limit reached');
      showLimitError('client');
      return false;
    }

    console.log('✅ Client limit check passed');
    return true;
  }, [user, restrictions, limits, navigate]);

  const checkOrderLimit = useCallback(async (): Promise<boolean> => {
    console.log('🔍 Checking order limit...');
    
    // Vérifier d'abord le rôle admin directement depuis la base de données
    let userIsAdmin = false;
    if (user && isSupabaseConfigured && supabase) {
      try {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!profileError && profile) {
          const normalizedRole = profile.role?.toLowerCase();
          userIsAdmin = ['admin', 'moderator'].includes(normalizedRole || '');
        }
      } catch (err) {
        console.error('Error checking admin role in checkOrderLimit:', err);
      }
    }
    
    // Si admin ou pas sur le plan free, autoriser
    if (userIsAdmin || restrictions?.isAdmin || restrictions?.plan !== 'free') {
      console.log('✅ User is admin or not on free plan, bypassing limit');
      return true;
    }

    // Refresh limits to get latest count
    await fetchLimits();
    
    if (!limits?.canAddOrder) {
      console.log('❌ Order limit reached');
      showLimitError('order');
      return false;
    }

    console.log('✅ Order limit check passed');
    return true;
  }, [user, restrictions, limits, navigate]);

  const refreshLimits = useCallback(async () => {
    await fetchLimits();
  }, [fetchLimits]);

  useEffect(() => {
    if (user && restrictions) {
      fetchLimits();
    }
  }, [user?.id, restrictions?.plan, fetchLimits]);

  return {
    limits,
    loading,
    error,
    checkClientLimit,
    checkOrderLimit,
    refreshLimits
  };
};